import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, FormHelperText, Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';

import { CreateButton } from '../../elements/CreateButton';
import UploadButton from '../../elements/Button/UploadButton';
import Input from '../../elements/input';
import CustomAutoComplete from '../../elements/AutoComplete';
import PhoneInput from '../../elements/Phone';
import Countries from '../../constants/country';
import States from '../../constants/state';

export default function JobApplicationModal(props) {
  const existingData = localStorage.getItem(`appliedJobData`) ? JSON.parse(localStorage.getItem(`appliedJobData`)) : {};
  const [formError, setFormError] = useState({});
  const [jobApplicationData, setJobApplicationData] = useState({
    ...existingData,
    candidateEmail: existingData?.email || "",
    phone: existingData?.mobile?.phone || "",
    phoneCode: existingData?.mobile?.phoneCode || ""
  });

  const {user, userAuthenticated} = useSelector((state) => {
    return {
      user: state?.auth?.user,
      userAuthenticated: state?.auth?.accessToken
    }
  });

  useEffect(() => {
      if (!!userAuthenticated) {
        const existingData = localStorage.getItem(`appliedJobData`) ? JSON.parse(localStorage.getItem(`appliedJobData`)) : {};
        setJobApplicationData({
          ...existingData,
          candidateEmail: user?.email,
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          phone: user?.mobile?.phone,
          phoneCode: user?.mobile?.phoneCode,
          country: user?.country || null,
          state: user?.state || '',
        });
      }
      return () => {};
  }, [userAuthenticated, user])

  const handleInputChange = (event) => {
    formError[event?.target?.name] = "";
    setFormError(formError);

    const changedPerks = { ...jobApplicationData };
    changedPerks[event?.target?.name] = event?.target?.value;
    setJobApplicationData(changedPerks);
  }

  const handleSelectChange = (event, values, name) => {
    formError[name] = "";
    setFormError(formError);
    const changedCandidateData = { ...jobApplicationData };
    if (name === 'country') {
      changedCandidateData.country = {
        ...changedCandidateData.country,
        name: values?.name,
        sortName: values?.sortname
      };
      changedCandidateData.state = "";
    } else if (name === 'state') {
      changedCandidateData.state = values?.name;
    }
    setJobApplicationData(changedCandidateData);
  }

  const handlePhoneChange = (event, data, e) => {
    formError['phone'] = "";
    setFormError(formError);
    const changedJobApplicationData = {...jobApplicationData};
    changedJobApplicationData.phone = event;
    changedJobApplicationData.phoneCode = data.dialCode;
    changedJobApplicationData.phoneString = e.target.value;
    changedJobApplicationData.phoneFormat = data?.format;
    changedJobApplicationData.countryCode = data?.countryCode;
    console.log(changedJobApplicationData)
    setJobApplicationData(changedJobApplicationData);
  }

  const handleResetPhoneChange = () => {
    formError['phone'] = "";
    setFormError(formError);
    const changedJobApplicationData = {...jobApplicationData};
    changedJobApplicationData.phone = "";
    changedJobApplicationData.phoneCode = "";
    changedJobApplicationData.phoneString = "";
    changedJobApplicationData.phoneFormat = "";
    changedJobApplicationData.sortName = "";
    changedJobApplicationData.countryCode = "";
    setJobApplicationData(changedJobApplicationData);
  }

  const handleJobApplicationSubmit = async () => {
    setFormError({});
    const newFormError = {};

    const emailCheckRegex = new RegExp(/^[\w.-]+@([\w]+\.)+[\w.]{2,3}$/gi);
    let isError = false;

    if (!jobApplicationData?.firstName) {
      newFormError.firstName = "Required"
      isError = true;
    }

    if (!(/^[a-zA-Z]/.test(jobApplicationData?.firstName))) {
      newFormError.firstName = "Enter valid first name"
      isError = true;
    }

    if (!jobApplicationData?.lastName) {
      newFormError.lastName = "Required"
      isError = true;
    }

    if (!(/^[a-zA-Z]/.test(jobApplicationData?.lastName))) {
      newFormError.lastName = "Enter valid last name"
      isError = true;
    }


    if (!jobApplicationData?.country?.name) {
      newFormError.country = "Select country"
      isError = true;
    }

    if (!jobApplicationData?.state) {
      newFormError.state = "Select state"
      isError = true;
    }
  
    if (!jobApplicationData?.candidateEmail) {
      newFormError.candidateEmail = "Email is required"
      isError = true;
    } else if (!emailCheckRegex.test(jobApplicationData?.candidateEmail)) {
      newFormError.candidateEmail = "Please enter a valid email."
      isError = true;
    }

    const splittedNumber  = jobApplicationData?.phoneString?.split(" ");
    let combinedNumber = '';
    if (!!splittedNumber?.length) {
      combinedNumber = [...splittedNumber.slice(1, splittedNumber.length)];
      combinedNumber = combinedNumber.join('');
    }

    if (!jobApplicationData?.phone) {
      newFormError.phone = "Phone number is required"
      isError = true;
    }

    if (jobApplicationData?.phone
      && jobApplicationData?.phone !== jobApplicationData?.phoneCode
      && jobApplicationData?.phoneString
      && combinedNumber.length < 6) {
      newFormError.phone = "Enter valid phone number"
      isError = true;
    }

    if (!jobApplicationData?.resume) {
      newFormError.resume = "Please upload Resume/CV"
      isError = true;
    }

    if (isError) {
      setFormError(newFormError);
      return;
    }

    await props?.handleSubmit(jobApplicationData);
  }

  const selectedCountry = Countries?.find(item => item?.sortname === jobApplicationData?.country?.sortName);
  const selectedStateOptions = States?.[selectedCountry?.id?.toString()] || [];
  const selectedState = selectedStateOptions?.find(item => item.name === jobApplicationData?.state);

  return (
    <div>
      <Dialog open={props?.isOpen} onClose={props?.handleClose} fullWidth maxWidth="md">
        <DialogTitle><h2><b>Apply for Job</b></h2></DialogTitle>
        <DialogContent>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Input
                  title="First name *"
                  name="firstName"
                  type="text"
                  variant="outlined"
                  placeholder="Enter First Name"
                  InputLabelProps={{
                    shrink: false,
                  }}
                  size="small"
                  value={jobApplicationData?.firstName || ""}
                  disabled={!!userAuthenticated}
                  onChange={handleInputChange}
                  error={formError?.firstName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  title="Last name *"
                  name="lastName"
                  type="text"
                  variant="outlined"
                  placeholder="Enter Last Name"
                  InputLabelProps={{
                    shrink: false,
                  }}
                  size="small"
                  value={jobApplicationData?.lastName || ""}
                  disabled={!!userAuthenticated}
                  onChange={handleInputChange}
                  error={formError?.lastName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  title="Email *"
                  name="candidateEmail"
                  type="text"
                  variant="outlined"
                  placeholder="Enter Email"
                  InputLabelProps={{
                    shrink: false,
                  }}
                  disabled={!!userAuthenticated}
                  size="small"
                  value={jobApplicationData?.candidateEmail || ""}
                  onChange={handleInputChange}
                  error={formError?.candidateEmail}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PhoneInput
                  title="Phone *"
                  country={jobApplicationData?.phone ? jobApplicationData?.country?.sortName?.toLowerCase() : 'in'}
                  value={jobApplicationData?.phone || ""}
                  onChange={(phoneNumber, data, event) => handlePhoneChange(phoneNumber, data, event)}
                  countryCodeEditable={false}
                  handleResetPhoneChange={handleResetPhoneChange}
                />
                {
                  formError?.phone ?
                    <FormHelperText className="error">
                      {formError?.phone}
                    </FormHelperText>
                  : null
                }
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomAutoComplete
                  id="country"
                  name="country"
                  options={Countries}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(event, values) => handleSelectChange(event, values, 'country')}
                  value={selectedCountry || {}}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      type="text"
                      variant="outlined"
                      title="Country *"
                      placeholder="Select Country"
                      InputLabelProps={{
                        shrink: false,
                      }}
                      size="small"
                    />
                  )}
                />
                {
                  formError?.country ?
                    <FormHelperText className="error">
                      {formError?.country}
                    </FormHelperText>
                  : null
                }
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomAutoComplete
                  id="state"
                  name="state"
                  options={selectedStateOptions || []}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(event, values) => handleSelectChange(event, values, 'state')}
                  value={selectedState || {}}
                  renderInput={(params) => (
                    <Input
                      {...params}
                      type="text"
                      name="state"
                      variant="outlined"
                      title="State *"
                      placeholder="Select state"
                      InputLabelProps={{
                        shrink: false,
                      }}
                      size="small"
                    />
                  )}
                />
                {
                  formError?.state ?
                    <FormHelperText className="error">
                      {formError?.state}
                    </FormHelperText>
                  : null
                }
              </Grid>
              <Grid item xs={12} md={12}>
                <UploadButton
                  title={"Resume/CV *"}
                  value={jobApplicationData?.resume}
                  onChange={(file) => handleInputChange({target: {name: "resume", value: file}})}
                  accept=".pdf,application/pdf"
                />
                {
                  formError?.resume ?
                    <FormHelperText className="error">
                      {formError?.resume}
                    </FormHelperText>
                  : null
                }
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions style={{ marginBottom: '16px', marginRight: '16px' }}>
          <Button variant="outlined" color="error" onClick={props?.handleClose} disabled={!!props?.isApplyingLoading}>Cancel</Button>
          <CreateButton variant="contained" onClick={handleJobApplicationSubmit} disabled={!!props?.isApplyingLoading}>
            { !!props?.isApplyingLoading ? <CircularProgress sx={{color: '#FFFFFF',}} size={24} color="inherit"/> : 'Apply' }
          </CreateButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
